import type { Middleware } from "@reduxjs/toolkit";
import { presentationChatApi } from "../services/presentationChat";
import { agentsApi } from "@frontdoor/agent-ui-assistive";

export const AGENT_TURN_COMPLETED_EVENT = "agent-turn-completed";

export interface IAgentTurnCompletedDetail {
  sessionId: string;
}

interface ISendMessageFulfilledAction {
  type: string;
  meta?: {
    arg?: {
      endpointName?: string;
      originalArgs?: { chatId?: string };
    };
  };
}

/**
 * Matched structurally rather than via `endpoints.sendMessage.matchFulfilled`
 * so the check works against any object shaped like the RTK Query fulfilled
 * action, without depending on the endpoint object exposing matcher helpers.
 */
const isSendMessageFulfilled = (
  action: unknown
): action is ISendMessageFulfilledAction => {
  const candidate = action as ISendMessageFulfilledAction | null;

  return (
    typeof candidate === "object" &&
    candidate !== null &&
    [agentsApi.reducerPath, presentationChatApi.reducerPath].some(
      (path) => candidate.type === `${path}/executeMutation/fulfilled`
    ) &&
    candidate.meta?.arg?.endpointName === "sendMessage"
  );
};

/**
 * Announce every completed agent chat turn as a window event.
 *
 * The RAAS portal keeps downstream milestone sessions in sync by pushing
 * derived parameters after an upstream agent changes project data. That sync
 * used to hang exclusively off the agent voluntarily emitting a
 * `task-accomplished` event in its response — prompts only mandate that emit
 * on the first accept/submit path, so post-approval edits saved artifacts
 * without any signal and downstream sessions (and the document viewer) went
 * stale (ESP-746).
 *
 * `agentsApi.endpoints.sendMessage` resolves only after the response stream
 * has been fully consumed, i.e. after every tool the agent ran (including
 * `save_agent_artifact`) has finished server-side. Announcing that moment
 * gives listeners a deterministic "the agent may have changed something"
 * signal that does not depend on the model remembering to emit an event.
 *
 * The generic agent API and the Presentation adapter each expose a sendMessage
 * mutation, so this middleware covers both without changing their transports. Listeners are expected
 * to treat this as a hint and dedupe their own work — pushes are already
 * payload-hash deduped, so turns that changed nothing produce no requests.
 */
export const agentTurnCompletedMiddleware: Middleware =
  () => (next) => (action) => {
    const result = next(action);

    if (isSendMessageFulfilled(action)) {
      const sessionId = action.meta?.arg?.originalArgs?.chatId;
      if (sessionId && typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent<IAgentTurnCompletedDetail>(
            AGENT_TURN_COMPLETED_EVENT,
            { detail: { sessionId } },
          ),
        );
      }
    }

    return result;
  };
